import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMainPageTitle } from '../../contexts/PageContext';
import { useAuth } from '../../contexts/authContext';
import EventsList from '../../components/player/events/EventsList';
import EventFilters from '../../components/player/events/EventFilters';
import EventStats from '../../components/player/events/EventStats';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1" || "http://localhost:3000/api/v1";

const Events = () => {
  const { t } = useTranslation();
  const { updatePageTitle } = useMainPageTitle();
  const { user } = useAuth();
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    eventType: '',
    eventStatus: '',
    page: 1,
    limit: 10,
    sortBy: 'startDate',
    sortOrder: 'asc'
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalEvents: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [stats, setStats] = useState({
    total: 0,
    upcoming: 0,
    completed: 0,
    inProgress: 0
  });

  useEffect(() => {
    updatePageTitle(t("player.events"), t("player.viewAllEvents"));
  }, [updatePageTitle, t]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const queryParams = new URLSearchParams({
        page: filters.page.toString(),
        limit: filters.limit.toString(),
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        ...(filters.eventType && { eventType: filters.eventType }),
        ...(filters.eventStatus && { eventStatus: filters.eventStatus })
      });

      const response = await fetch(`${API_URL}/event/get-all-events-by-user?${queryParams}`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      let data;
      
      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle "no events found" as a successful empty state, not an error
        if (response.status === 404 && errorData.error === "NO_EVENTS_FOUND") {
          data = {
            success: true,
            events: [],
            pagination: errorData.pagination || {
              page: filters.page,
              limit: filters.limit,
              totalPages: 0,
              total: 0,
              hasNext: false,
              hasPrev: false
            },
            stats: {
              upcoming: 0,
              completed: 0,
              inProgress: 0
            }
          };
        } else {
          throw new Error(errorData.message || "Failed to fetch events");
        }
      } else {
        data = await response.json();
      }
      
      setEvents(data.events || []);
      setPagination({
        currentPage: data.pagination?.page || filters.page,
        totalPages: data.pagination?.totalPages || 1,
        totalEvents: data.pagination?.total || 0,
        hasNextPage: data.pagination?.hasNext || false,
        hasPrevPage: data.pagination?.hasPrev || false
      });

      // Calculate stats
      const eventStats = {
        total: data.pagination?.total || 0,
        upcoming: data.summary?.upcomingEvents || 0,
        completed: data.summary?.completedEvents || 0,
        inProgress: data.summary?.inProgressEvents || 0
      };
      setStats(eventStats);

    } catch (err) {
      console.error("Error fetching events:", err);
      setError(err.message || "Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [filters]);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleRefresh = () => {
    fetchEvents();
  };

  if (loading && events.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <LoadingSpinner message={t("player.loadingEvents")} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            {t("player.myEvents")}
          </h1>
          <p className="text-lg text-gray-600">
            {t("player.eventsDescription")}
          </p>
        </div>

        {/* Stats Section */}
        <EventStats stats={stats} />

        {/* Filters Section */}
        <EventFilters 
          filters={filters}
          onFilterChange={handleFilterChange}
          onRefresh={handleRefresh}
          loading={loading}
        />

        {/* Error Message */}
        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} onRetry={handleRefresh} />
          </div>
        )}

        {/* Events List */}
        <EventsList 
          events={events}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onRefresh={handleRefresh}
        />
      </div>
    </div>
  );
};

export default Events; 